import React, { memo } from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";

export type StepStatus = "completed" | "current" | "pending" | "failed";

export interface Step {
  /** Primary label for this step (translation key, e.g. "vendor.step.createAccount"). */
  label: TranslationKeys;
  /** Secondary descriptor shown below the label (translation key, e.g. "common.completed"). */
  description?: TranslationKeys;
  status: StepStatus;
}

export interface StepIndicatorProps {
  steps: readonly Step[];
  /** Forwarded to the root container for testing. */
  testID?: string;
  /**
   * Custom icon rendered inside the `completed` step circle.
   * Defaults to a "✓" character via AppText.
   */
  icon?: React.ReactNode;
  /**
   * Layout direction.
   * - `"vertical"` (default) — circles on the left, labels on the right, connector lines go down.
   * - `"horizontal"` — circles on top, labels below, connector lines go right (web pending-review).
   */
  orientation?: "vertical" | "horizontal";
  /**
   * Visual variant.
   * - `"default"` — primary-color current dot, solid green/gray connectors.
   * - `"dashboard"` — success-color dashed current dot, semantic connectors
   *   (red after completed→failed, dashed-green after completed→non-completed),
   *   and larger `text-base` labels matching the vendor dashboard stepper.
   */
  variant?: "default" | "dashboard";
}

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * Visual height of the connector line between two step circles.
 * Set to 48px — a comfortable spacer that aligns with two-line label blocks
 * (title 16px + description 14px ≈ 43px plus gaps).
 */
const CONNECTOR_HEIGHT = 48;

/**
 * Outer circle diameter: inner 20px + 2×border 2px + 2×padding 4px = 32px.
 */
const COLUMN_WIDTH = 32;

/**
 * Inner icon size: 20px (Figma circle diameter) minus 2×padding 4px = 12px.
 * If the icon is smaller than this it will be centered with extra padding around it;
 * if larger, it will overflow the circle.
 */
const ICON_SIZE = 20;

// ─── Sub-components ──────────────────────────────────────────────────────────

function StepDot({
  status,
  icon,
  variant = "default",
}: {
  status: StepStatus;
  icon?: React.ReactNode;
  variant?: "default" | "dashboard";
}) {
  if (status === "completed") {
    return (
      <View
        className="rounded-full border-2 border-success bg-success p-1 items-center justify-center"
        accessible={false}
      >
        <View className="size-5 rounded-full bg-success items-center justify-center">
          {icon != null ? (
            icon
          ) : (
            <AppText
              weight="bold"
              className="text-white text-xs leading-none"
              accessibilityElementsHidden
              importantForAccessibility="no"
            >
              ✓
            </AppText>
          )}
        </View>
      </View>
    );
  }

  if (status === "failed") {
    return (
      <View
        className="rounded-full border-2 border-error bg-error p-1 items-center justify-center"
        accessible={false}
      >
        <View className="size-5 rounded-full bg-error items-center justify-center">
          {icon != null ? (
            icon
          ) : (
            <AppText
              weight="bold"
              className="text-white text-xs leading-none"
              accessibilityElementsHidden
              importantForAccessibility="no"
            >
              ✕
            </AppText>
          )}
        </View>
      </View>
    );
  }

  if (status === "current") {
    if (variant === "dashboard") {
      // Dashed success-color border matching the Figma vendor dashboard stepper.
      return (
        <View
          className="rounded-full border-2 border-success p-1 items-center justify-center"
          style={{ borderStyle: "dashed" }}
          accessible={false}
        >
          <View className="size-5 rounded-full bg-success" />
        </View>
      );
    }
    // Outlined ring with primary-color border — the active in-progress step.
    return (
      <View
        className="rounded-full border-2 border-primary bg-white p-1 items-center justify-center"
        accessible={false}
      >
        <View className="size-5 rounded-full bg-primary" />
      </View>
    );
  }

  // pending — soft gray ring with filled gray inner circle
  return (
    <View
      className="rounded-full border-2 border-stroke-50 bg-white p-1 items-center justify-center"
      accessible={false}
    >
      {/* Figma: Supporting Text/05 (#CACDD3) — closest available: stroke-50 */}
      <View className="size-5 rounded-full bg-stroke-50" />
    </View>
  );
}

function ConnectorLine({ fromStatus }: { fromStatus: StepStatus }) {
  // Green line after a completed step; neutral gray otherwise.
  const bgClass = fromStatus === "completed" ? "bg-success" : "bg-stroke-50";
  return (
    <View
      accessibilityElementsHidden
      importantForAccessibility="no"
      className={`w-0.5 ${bgClass}`}
      style={{ height: CONNECTOR_HEIGHT }}
    />
  );
}

/**
 * Returns visual-only style (height + colour/border) for a horizontal connector
 * between two adjacent steps. Does NOT include layout props (flex, width, position).
 */
function getConnectorVisualStyle(
  fromStatus: StepStatus,
  toStatus: StepStatus,
  variant: "default" | "dashboard"
): object {
  if (variant === "dashboard") {
    const isRed = fromStatus === "completed" && toStatus === "failed";
    const isSolidGreen = fromStatus === "completed" && toStatus === "completed";
    const isDashedGreen = fromStatus === "completed" && !isRed && !isSolidGreen;
    if (isRed) return { height: 2, backgroundColor: "#E5000B" };
    if (isSolidGreen) return { height: 2, backgroundColor: "#009942" };
    if (isDashedGreen)
      return {
        height: 0,
        borderTopWidth: 2,
        borderStyle: "dashed" as const,
        borderColor: "#009942",
      };
    return { height: 2, backgroundColor: "#CACDD3" };
  }
  return {
    height: 2,
    backgroundColor: fromStatus === "completed" ? "#009942" : "#CACDD3",
  };
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Vertical step-progress indicator extracted from the Figma completion screen.
 *
 * Renders a column of step circles connected by lines on the left, with
 * corresponding label + description text blocks on the right.
 *
 * Three step states are supported:
 * - `completed` — green filled circle with a check icon.
 * - `current`   — primary-color filled ring (in-progress).
 * - `pending`   — soft gray ring.
 *
 * The connector line following a `completed` step is green; all others are
 * the neutral `stroke-50` color.
 *
 * @example
 * <StepIndicator
 *   steps={[
 *     { label: 'Create Account',    description: 'Completed', status: 'completed' },
 *     { label: 'Submit Application',description: 'Completed', status: 'completed' },
 *     { label: 'Approve Application',description: 'Pending', status: 'pending' },
 *   ]}
 * />
 */
export const StepIndicator = memo(function StepIndicator({
  steps,
  testID,
  icon,
  orientation = "vertical",
  variant = "default",
}: StepIndicatorProps) {
  const { t } = useI18n();

  // ─── Horizontal layout ───────────────────────────────────────────────────────
  if (orientation === "horizontal") {
    const dotRadius = COLUMN_WIDTH / 2;
    return (
      <View testID={testID} className="w-full">
        <View style={{ flexDirection: "row" }}>
          {steps.map((step, i) => {
            const isFirst = i === 0;
            const isLast = i === steps.length - 1;
            const prevStep = steps[i - 1];
            const nextStep = steps[i + 1];

            return (
              <View key={i} style={isFirst || isLast ? undefined : { flex: 1 }}>
                {/* ── Dot + half-connectors ──────────────────────── */}
                <View
                  style={{
                    height: COLUMN_WIDTH,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {/* Left half-connector (left edge → cell centre) */}
                  {!isFirst && (
                    <View
                      accessibilityElementsHidden
                      importantForAccessibility="no"
                      style={{
                        position: "absolute",
                        left: 0,
                        right: "50%",
                        top: dotRadius - 1,
                        ...getConnectorVisualStyle(prevStep!.status, step.status, variant),
                      }}
                    />
                  )}
                  {/* Right half-connector (cell centre → right edge) */}
                  {!isLast && (
                    <View
                      accessibilityElementsHidden
                      importantForAccessibility="no"
                      style={{
                        position: "absolute",
                        left: "50%",
                        right: 0,
                        top: dotRadius - 1,
                        ...getConnectorVisualStyle(step.status, nextStep!.status, variant),
                      }}
                    />
                  )}
                  {/* Dot rendered on top of connectors */}
                  <View style={{ zIndex: 1 }}>
                    <StepDot status={step.status} icon={icon} variant={variant} />
                  </View>
                </View>

                {/* ── Label centred under dot ────────────────────── */}
                <View
                  style={{
                    marginTop: variant === "dashboard" ? 16 : 12,
                    alignItems: "center",
                  }}
                >
                  {variant === "dashboard" ? (
                    <AppText weight="medium" className="text-base text-supporting-text text-center">
                      {t(step.label)}
                    </AppText>
                  ) : (
                    <AppText weight="semibold" className="text-sm text-dark-text-50 text-center">
                      {t(step.label)}
                    </AppText>
                  )}
                  {step.description != null && (
                    <AppText
                      weight="medium"
                      className="text-xs text-supporting-text mt-0.5 text-center"
                    >
                      {t(step.description)}
                    </AppText>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      </View>
    );
  }

  // ─── Vertical layout (default) ────────────────────────────────────────────
  return (
    <View className="flex-row gap-4" testID={testID}>
      {/* ── Left: circles + connector lines ─────────────────────────── */}
      <View style={{ width: COLUMN_WIDTH }} className="items-center">
        {steps.map((step, i) => (
          <React.Fragment key={i}>
            <StepDot status={step.status} icon={icon} />
            {i < steps.length - 1 && <ConnectorLine fromStatus={step.status} />}
          </React.Fragment>
        ))}
      </View>

      {/* ── Right: labels ────────────────────────────────────────────── */}
      <View className="flex-1">
        {steps.map((step, i) => (
          <View
            key={i}
            style={{ marginBottom: i < steps.length - 1 ? CONNECTOR_HEIGHT - ICON_SIZE : 0 }}
          >
            <AppText
              weight="semibold"
              className="text-base text-dark-text-50"
              accessibilityRole="text"
            >
              {t(step.label)}
            </AppText>
            {step.description != null && (
              <AppText weight="medium" className="text-sm text-supporting-text mt-0.5">
                {t(step.description)}
              </AppText>
            )}
          </View>
        ))}
      </View>
    </View>
  );
});
